import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, FormControl,
  Select, MenuItem, CircularProgress
} from '@mui/material';
import {
  Download, PictureAsPdf, Person,
  Business, AssignmentInd, ReceiptLong
} from '@mui/icons-material';
import ExportButtons from '../btns/ExportButtons';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getAllEmployees } from '../../services/HRstaff';
import { getAllContracts } from '../../services/ContractAPI';
import { getAllCandidates } from '../../services/onboardingapi';
import PageHeader from '../headers/Header';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, ChartDataLabels);

// Enhanced chart options for horizontal bar charts
const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
};

const horizontalChartOptions = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(31, 41, 55, 0.9)',
      titleFont: { size: 14 },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
    },
    datalabels: {
      anchor: 'end',
      align: 'right',
      color: '#1f2937',
      font: {
        weight: 'bold',
        size: 12,
      },
      formatter: (value) => value,
    },
  },
  scales: {
    x: {
      ticks: { color: '#1f2937' },
      grid: { color: 'rgba(226, 232, 240, 0.3)' },
      beginAtZero: true,
    },
    y: {
      ticks: { color: '#1f2937', font: { size: 12 } },
      grid: { display: false },
    },
  },
  layout: {
    padding: {
      right: 40,
    },
  },
};

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        font: { size: 12 },
        color: '#1f2937',
        padding: 15,
        usePointStyle: true,
        pointStyle: 'circle',
      },
    },
    tooltip: {
      backgroundColor: 'rgba(31, 41, 55, 0.9)',
      titleFont: { size: 14 },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: function (context) {
          const label = context.label || '';
          const value = context.parsed;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1);
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    },
    datalabels: {
      display: true,
      color: 'white',
      font: {
        weight: 'bold',
        size: 12,
      },
      formatter: (value, context) => {
        const total = context.dataset.data.reduce((a, b) => a + b, 0);
        const percentage = ((value / total) * 100).toFixed(1);
        return value > 0 ? `${value}
(${percentage}%)` : '';
      },
    },
  },
};

const EmployeeOverview = () => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [filters, setFilters] = useState({
    office: 'all',
    department: 'all',
    contractType: 'all',
    dateRange: 'all',
  });
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [kpiData, setKpiData] = useState({
    totalEmployees: 0,
    newHires: 0,
    averageTenure: { years: 0, months: 0 },
  });

  // Chart refs
  const departmentChartRef = useRef(null);
  const positionChartRef = useRef(null);
  const contractTypeChartRef = useRef(null);
  const officeLocationChartRef = useRef(null);
  const ageDistributionChartRef = useRef(null);

  // Refs for PDF export sections
  const kpiSectionRef = useRef(null);
  const chartsRowRef = useRef(null);
  const analyticsRef = useRef(null);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (employees.length > 0 && candidates.length > 0) {
      applyFilters();
    }
  }, [employees, candidates, filters]);

  useEffect(() => {
    if (filteredEmployees.length > 0) {
      calculateKPIs();
    }
  }, [filteredEmployees, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesResponse, contractsResponse, candidatesResponse] = await Promise.all([
        getAllEmployees(),
        getAllContracts(),
        getAllCandidates(),
      ]);
      setEmployees(employeesResponse.data || employeesResponse);
      setContracts(contractsResponse.data || contractsResponse);
      const transformedCandidates = (candidatesResponse.data || candidatesResponse).map(candidate => ({
        ...candidate,
        status: transformStatus(candidate.status),
        onboardingProgress: candidate.onboardingProgress || 0,
      }));
      setCandidates(transformedCandidates);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const transformStatus = (apiStatus) => {
    const statusMap = {
      'NOT_PLANNED_YET': 'Not Planned Yet',
      'PENDING': 'Pending',
      'INTERVIEW_2': '2nd Interview',
      'INTERVIEW_3': '3rd Interview',
      'HIRED': 'Hired',
      'DECLINED_BY_HR': 'Declined by HR',
      'DECLINED_BY_MANAGER': 'Declined by Manager',
      'RESERVED_LIST': 'Reserved List',
      'DECLINED_OFFER': 'Declined Offer',
      'Candidate_Declined_the_Offre': 'Declined Offer',
    };
    return statusMap[apiStatus] || 'Not Planned Yet';
  };

  const applyFilters = () => {
    let filtered = [...employees];
    if (filters.office !== 'all') {
      filtered = filtered.filter(e => e.office?.name === filters.office);
    }
    if (filters.department !== 'all') {
      filtered = filtered.filter(e => e.department?.name === filters.department);
    }
    if (filters.contractType !== 'all') {
      filtered = filtered.filter(e => {
        const employeeContract = contracts.find(c => c.employeeId === e.id || c.employeeId === e.employeeId);
        return employeeContract?.type === filters.contractType;
      });
    }
    setFilteredEmployees(filtered);
  };

  const calculateKPIs = () => {
    const total = filteredEmployees.length;
    const now = new Date();
    let newHires = candidates.filter(c => c.status === 'Hired' && c.onboardingProgress >= 0);
    let newHiresCount = newHires.length;
    if (filters.dateRange === 'thisMonth') {
      newHiresCount = newHires.filter(c => {
        const joinDate = new Date(c.createdAt);
        return joinDate.getMonth() === now.getMonth() && joinDate.getFullYear() === now.getFullYear();
      }).length;
    } else if (filters.dateRange === 'thisQuarter') {
      newHiresCount = newHires.filter(c => {
        const joinDate = new Date(c.createdAt);
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const joinQuarter = Math.floor(joinDate.getMonth() / 3);
        return joinQuarter === currentQuarter && joinDate.getFullYear() === now.getFullYear();
      }).length;
    }
    const activeEmployees = filteredEmployees.filter(e => !e.terminationDate);
    let totalTenureMonths = 0;
    let employeeCount = 0;
    activeEmployees.forEach(e => {
      const joinDate = new Date(e.dateOfJoining || e.createdDate);
      if (!isNaN(joinDate.getTime())) {
        const diffTime = Math.abs(now - joinDate);
        const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30.44));
        totalTenureMonths += diffMonths;
        employeeCount++;
      }
    });
    const averageMonths = employeeCount > 0 ? totalTenureMonths / employeeCount : 0;
    const years = Math.floor(averageMonths / 12);
    const months = Math.floor(averageMonths % 12);
    setKpiData({
      totalEmployees: total,
      newHires: newHiresCount,
      averageTenure: { years, months },
    });
  };

  const getUniqueValues = (key, subKey = null) => {
    const values = employees.map(e => subKey ? e[key]?.[subKey] : e[key]).filter(Boolean);
    return [...new Set(values)];
  };

  const getDepartmentChartData = () => {
    const deptMap = {};
    filteredEmployees.forEach(e => {
      const deptName = e.departmentName || 'Unknown';
      deptMap[deptName] = (deptMap[deptName] || 0) + 1;
    });
    return {
      labels: Object.keys(deptMap),
      datasets: [{
        label: 'Employees by Department',
        data: Object.values(deptMap),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(59, 130, 246, 0.9)',
      }],
    };
  };

  const getPositionChartData = () => {
    const posMap = {};
    filteredEmployees.forEach(e => {
      if (Array.isArray(e.positions) && e.positions.length > 0) {
        e.positions.forEach(p => {
          const posName = p.positionName || 'Unknown';
          posMap[posName] = (posMap[posName] || 0) + (parseFloat(p.fte) || 1.0);
        });
      } else {
        // Fallback for employees without a positions array
        const posName = e.position || 'Unknown';
        posMap[posName] = (posMap[posName] || 0) + 1;
      }
    });
    return {
      labels: Object.keys(posMap),
      datasets: [{
        label: 'Employees by Position',
        data: Object.values(posMap),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(16, 185, 129, 0.9)',
      }],
    };
  };

  const getContractTypeChartData = () => {
    const contractMap = {};
    const contractTypes = ['CDI', 'CDD', 'Internship', 'PSC', 'ANAPEC'];
    contractTypes.forEach(type => contractMap[type] = 0);
    contracts.forEach(c => {
      if (contractMap[c.type] !== undefined) {
        contractMap[c.type]++;
      }
    });
    return {
      labels: Object.keys(contractMap),
      datasets: [{
        label: 'Employees by Contract Type',
        data: Object.values(contractMap),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(156, 39, 176, 0.8)',
        ],
        hoverBackgroundColor: [
          'rgba(59, 130, 246, 0.9)',
          'rgba(16, 185, 129, 0.9)',
          'rgba(245, 158, 11, 0.9)',
          'rgba(156, 39, 176, 0.9)',
        ],
        borderWidth: 1,
      }],
    };
  };

  const getOfficeLocationChartData = () => {
    const officeMap = {};
    filteredEmployees.forEach(e => {
      const officeName = e.office?.name || 'Unknown';
      officeMap[officeName] = (officeMap[officeName] || 0) + 1;
    });
    return {
      labels: Object.keys(officeMap),
      datasets: [{
        label: 'Employees by Office Location',
        data: Object.values(officeMap),
        backgroundColor: 'rgba(233, 30, 99, 0.8)',
        borderColor: 'rgba(233, 30, 99, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(233, 30, 99, 0.9)',
      }],
    };
  };

  const getAgeDistributionChartData = () => {
    const ageGroups = {
      'Under 20': 0,
      '20 - 25': 0,
      '26 - 30': 0,
      '31 - 35': 0,
      '36 - 40': 0,
      '41 - 50': 0,
      '50+': 0,
    };
    let notSpecified = 0;

    filteredEmployees.forEach(e => {
      const age = calculateAge(e.dob);
      if (typeof age !== 'number' || isNaN(age) || age <= 0) {
        notSpecified++;
      } else if (age < 20) {
        ageGroups['Under 20']++;
      } else if (age <= 25) {
        ageGroups['20 - 25']++;
      } else if (age <= 30) {
        ageGroups['26 - 30']++;
      } else if (age <= 35) {
        ageGroups['31 - 35']++;
      } else if (age <= 40) {
        ageGroups['36 - 40']++;
      } else if (age <= 50) {
        ageGroups['41 - 50']++;
      } else {
        ageGroups['50+']++;
      }
    });

    const labels = Object.keys(ageGroups);
    const data = Object.values(ageGroups);
    if (notSpecified > 0) {
      labels.push('Not Specified');
      data.push(notSpecified);
    }

    const backgroundColors = [
      'rgba(99, 102, 241, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(14, 165, 233, 0.8)',
      'rgba(16, 185, 129, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(249, 115, 22, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(156, 163, 175, 0.8)',
    ].slice(0, labels.length);

    return {
      labels,
      datasets: [{
        label: 'Employees by Age',
        data,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        hoverBackgroundColor: backgroundColors.map(c => c.replace('0.8', '0.95')),
      }],
    };
  };

  const averageAge = useMemo(() => {
    const validAges = filteredEmployees
      .map(e => calculateAge(e.dob))
      .filter(a => typeof a === 'number' && a > 0);
    if (validAges.length === 0) return null;
    const sum = validAges.reduce((acc, c) => acc + c, 0);
    return (sum / validAges.length).toFixed(1);
  }, [filteredEmployees]);


  // Add footer with pagination function (unchanged)
  const addFooterWithPagination = (doc, currentPage, totalPages) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    // Footer background
    doc.setFillColor(248, 250, 252);
    doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');

    // Page number with system name
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${currentPage} of ${totalPages} | AxoPeople`, pageWidth / 2, pageHeight - 8, { align: 'center' });

    // Confidential marking
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Employee Overview Report - Confidential', margin, pageHeight - 8);

    // Generation timestamp
    const today = new Date().toLocaleDateString();
    doc.text(`Generated: ${today}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  };

  // Visual PDF generation function (updated for high-resolution first page)
  const generateVisualPDF = async () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const today = new Date().toLocaleDateString();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;

      // Track total pages for pagination
      let totalPages = 1; // Start with 1 for the first page
      let currentY = 80;

      // Title
      doc.setFontSize(24);
      doc.setTextColor(59, 130, 246);
      doc.text('Employee Overview Report', pageWidth / 2, 40, { align: 'center' });
      doc.setFontSize(16);
      doc.text(`Visual Dashboard Export`, pageWidth / 2, 55, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(`Generated: ${today}`, pageWidth / 2, 70, { align: 'center' });

      // Define sections to capture (high-resolution for first section only)
      const sections = [
        {
          ref: kpiSectionRef,
          title: 'Key Performance Indicators',
          scale: 4, // Increased for high-resolution first page
          maxImgHeight: 120 // Increased to accommodate high-res image
        },
        {
          ref: chartsRowRef,
          title: 'Distribution Analysis - Part 1',
          scale: 1.5, // Original scale for lower quality
          maxImgHeight: 200 // Original max height
        },
        {
          ref: analyticsRef,
          title: 'Distribution Analysis - Part 2',
          scale: 1.5, // Original scale for lower quality
          maxImgHeight: 200 // Original max height
        }
      ];

      // Capture and add each section to PDF
      for (const section of sections) {
        if (!section.ref || !section.ref.current) continue;

        // Capture the section as an image
        const canvas = await html2canvas(section.ref.current, {
          backgroundColor: 'var(--card-bg, white)',
          scale: section.scale, // High scale for KPI section, lower for others
          useCORS: true,
          allowTaint: true,
          height: section.ref.current.scrollHeight,
          width: section.ref.current.scrollWidth,
        });

        const imgData = canvas.toDataURL('image/png', 1.0); // Full quality for images
        let imgHeight = (canvas.height * (pageWidth - margin * 2)) / canvas.width;

        if (imgHeight > section.maxImgHeight) {
          imgHeight = section.maxImgHeight;
        }

        const sectionHeight = 10 + imgHeight + 10; // title + image + margin

        // Add new page if needed
        if (currentY + sectionHeight > pageHeight - 30) { // Reserve space for footer
          doc.addPage();
          totalPages++; // Increment totalPages for new page
          currentY = 20;
        }

        // Add section title
        doc.setFontSize(18);
        doc.setTextColor(59, 130, 246);
        doc.text(section.title, margin, currentY);
        currentY += 10;

        // Add image (high-res for KPI section)
        doc.addImage(imgData, 'PNG', margin, currentY, pageWidth - margin * 2, imgHeight);
        currentY += imgHeight + 10;
      }

      // Add detailed employee table
      if (filteredEmployees.length > 0) {
        const tableSectionHeight = 20 + 20 + filteredEmployees.length * 5;
        if (currentY + tableSectionHeight > pageHeight - 30) { // Reserve space for footer
          doc.addPage();
          totalPages++; // Increment totalPages for new page
          currentY = 20;
        }
        doc.setFontSize(18);
        doc.setTextColor(59, 130, 246);
        doc.text(`Employee Details (${filteredEmployees.length} records)`, margin, currentY);

        // Prepare table data
        const tableData = filteredEmployees.map(emp => {
          const employeeContract = contracts.find(c => c.employeeId === emp.id || c.employeeId === emp.employeeId);
          return [
            `${emp.firstName || ''} ${emp.lastName || ''}`,
            emp.position || 'N/A',
            emp.department?.name || 'N/A',
            emp.office?.name || 'N/A',
            employeeContract?.type || 'N/A',
            emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString() : 'N/A',
          ];
        });

        // Add table and track additional pages created by autoTable
        autoTable(doc, {
          startY: currentY + 15,
          head: [['Employee Name', 'Position', 'Department', 'Office', 'Contract Type', 'Join Date']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: [255, 255, 255],
            fontSize: 9,
          },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
          didDrawPage: () => {
            // Update totalPages dynamically during table rendering
            totalPages = doc.internal.getNumberOfPages();
          },
        });

        // Update totalPages after table rendering
        totalPages = doc.internal.getNumberOfPages();
      }

      // Add footers to all pages
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        doc.setPage(pageNum);
        addFooterWithPagination(doc, pageNum, totalPages);
      }

      // Save the PDF
      const filename = `employee-overview-visual-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

    } catch (error) {
      console.error('Error generating visual PDF:', error);
      alert('Error generating visual PDF. Please try again.');
    }
  };
  // Simple PDF generation function (data-only) - kept as fallback
  const generatePDF = () => {
    const doc = new jsPDF();
    // Title and header
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210); // Blue color for title
    doc.text('Employee Overview Report', 20, 20);
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 30);
    // Filter information
    const filterText = `Filters: Office=${filters.office === 'all' ? 'All' : filters.office}, Department=${filters.department === 'all' ? 'All' : filters.department}, Contract=${filters.contractType === 'all' ? 'All' : filters.contractType}, Date Range=${filters.dateRange === 'all' ? 'All Time' : filters.dateRange}`;
    doc.text(filterText, 20, 40);
    // KPI Summary
    doc.text(`Total Employees: ${kpiData.totalEmployees}`, 20, 50);
    doc.text(`New Hires: ${kpiData.newHires}`, 20, 60);
    doc.text(`Average Tenure: ${kpiData.averageTenure.years}y ${kpiData.averageTenure.months}m`, 20, 70);
    // Prepare table data
    const tableData = filteredEmployees.map(emp => {
      const employeeContract = contracts.find(c => c.employeeId === emp.id || c.employeeId === emp.employeeId);
      return [
        `${emp.firstName || ''} ${emp.lastName || ''}`,
        emp.position || 'N/A',
        emp.department?.name || 'N/A',
        emp.office?.name || 'N/A',
        employeeContract?.type || 'N/A',
        emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString() : 'N/A',
      ];
    });
    // Create table
    autoTable(doc, {
      startY: 80,
      head: [['Employee Name', 'Position', 'Department', 'Office', 'Contract Type', 'Join Date']],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 10, cellPadding: 5 },
      headStyles: { fillColor: [25, 118, 210], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });
    // Save the PDF
    doc.save('employee-overview-report.pdf');
  };

  // Fixed Excel generation function
  const generateExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const sheetName = 'Employee Overview';
      const summaryData = [
        ['EMPLOYEE OVERVIEW REPORT', '', '', '', '', ''],
        ['Generated:', new Date().toLocaleDateString(), '', '', '', ''],
        ['Filters:', `Office: ${filters.office}, Department: ${filters.department}, Contract: ${filters.contractType}, Date Range: ${filters.dateRange}`, '', '', '', ''],
        [],
        ['KEY PERFORMANCE INDICATORS', '', '', '', '', ''],
        ['Metric', 'Value', '', '', '', ''],
        ['Total Employees', kpiData.totalEmployees, '', '', '', ''],
        ['New Hires', kpiData.newHires, '', '', '', ''],
        ['Average Tenure', `${kpiData.averageTenure.years} years ${kpiData.averageTenure.months} months`, '', '', '', ''],
        [],
        ['DEPARTMENT DISTRIBUTION', '', '', '', '', ''],
      ];
      const deptData = getDepartmentChartData();
      deptData.labels.forEach((label, index) => {
        summaryData.push([label, deptData.datasets[0].data[index], '', '', '', '']);
      });
      summaryData.push([]);
      summaryData.push(['POSITION DISTRIBUTION', '', '', '', '', '']);
      const posData = getPositionChartData();
      posData.labels.forEach((label, index) => {
        summaryData.push([label, posData.datasets[0].data[index], '', '', '', '']);
      });
      summaryData.push([]);
      summaryData.push(['OFFICE DISTRIBUTION', '', '', '', '', '']);
      const officeData = getOfficeLocationChartData();
      officeData.labels.forEach((label, index) => {
        summaryData.push([label, officeData.datasets[0].data[index], '', '', '', '']);
      });
      summaryData.push([]);
      summaryData.push(['CONTRACT TYPE DISTRIBUTION', '', '', '', '', '']);
      const contractData = getContractTypeChartData();
      contractData.labels.forEach((label, index) => {
        summaryData.push([label, contractData.datasets[0].data[index], '', '', '', '']);
      });
      const ws = XLSX.utils.aoa_to_sheet(summaryData);
      ws['!cols'] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 }
      ];
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = 0; C <= 5; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
        ws[cellAddress].s = {
          font: { bold: true, sz: 16, color: { rgb: "3B82F6" } },
          fill: { fgColor: { rgb: "F3F4F6" } },
          alignment: { horizontal: "center" }
        };
      }
      const sectionRows = [4, 9, 10 + deptData.labels.length, 11 + deptData.labels.length + posData.labels.length, 12 + deptData.labels.length + posData.labels.length + officeData.labels.length];
      sectionRows.forEach(row => {
        for (let C = 0; C <= 5; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: C });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          ws[cellAddress].s = {
            font: { bold: true, sz: 12, color: { rgb: "3B82F6" } },
            fill: { fgColor: { rgb: "DBEAFE" } },
            alignment: { horizontal: "left" }
          };
        }
      });
      for (let C = 0; C <= 5; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 5, c: C });
        if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
        ws[cellAddress].s = {
          font: { bold: true, sz: 11, color: { rgb: "1F2937" } },
          fill: { fgColor: { rgb: "EFF6FF" } },
          alignment: { horizontal: "left" }
        };
      }
      for (let R = 6; R <= 8; R++) {
        for (let C = 0; C <= 5; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          ws[cellAddress].s = {
            font: { sz: 11, color: { rgb: "1F2937" } },
            fill: { fgColor: { rgb: "FFFFFF" } },
            alignment: { horizontal: C === 0 ? "left" : "right" }
          };
          if (C === 0) {
            ws[cellAddress].s.font.bold = true;
          }
        }
      }
      let startRow = 10;
      for (let i = 0; i < deptData.labels.length; i++) {
        const row = startRow + i;
        for (let C = 0; C <= 5; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: C });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          ws[cellAddress].s = {
            font: { sz: 11, color: { rgb: "1F2937" } },
            fill: { fgColor: { rgb: i % 2 === 0 ? "FFFFFF" : "F9FAFB" } },
            alignment: { horizontal: C === 0 ? "left" : "right" }
          };
          if (C === 0) {
            ws[cellAddress].s.font.bold = true;
          }
        }
      }
      startRow = 11 + deptData.labels.length;
      for (let i = 0; i < posData.labels.length; i++) {
        const row = startRow + i;
        for (let C = 0; C <= 5; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: C });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          ws[cellAddress].s = {
            font: { sz: 11, color: { rgb: "1F2937" } },
            fill: { fgColor: { rgb: i % 2 === 0 ? "FFFFFF" : "F9FAFB" } },
            alignment: { horizontal: C === 0 ? "left" : "right" }
          };
          if (C === 0) {
            ws[cellAddress].s.font.bold = true;
          }
        }
      }
      startRow = 12 + deptData.labels.length + posData.labels.length;
      for (let i = 0; i < officeData.labels.length; i++) {
        const row = startRow + i;
        for (let C = 0; C <= 5; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: C });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          ws[cellAddress].s = {
            font: { sz: 11, color: { rgb: "1F2937" } },
            fill: { fgColor: { rgb: i % 2 === 0 ? "FFFFFF" : "F9FAFB" } },
            alignment: { horizontal: C === 0 ? "left" : "right" }
          };
          if (C === 0) {
            ws[cellAddress].s.font.bold = true;
          }
        }
      }
      startRow = 13 + deptData.labels.length + posData.labels.length + officeData.labels.length;
      for (let i = 0; i < contractData.labels.length; i++) {
        const row = startRow + i;
        for (let C = 0; C <= 5; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: C });
          if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
          ws[cellAddress].s = {
            font: { sz: 11, color: { rgb: "1F2937" } },
            fill: { fgColor: { rgb: i % 2 === 0 ? "FFFFFF" : "F9FAFB" } },
            alignment: { horizontal: C === 0 ? "left" : "right" }
          };
          if (C === 0) {
            ws[cellAddress].s.font.bold = true;
          }
        }
      }
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const detailsSheetName = 'Employee Details';
      const detailsData = [
        ['EMPLOYEE DETAILS', '', '', '', '', '', '', ''],
        [],
        ['Name', 'Position', 'Department', 'Office', 'Contract Type', 'Join Date', 'Email', 'Phone'],
        ...filteredEmployees.map(emp => {
          const employeeContract = contracts.find(c => c.employeeId === emp.id || c.employeeId === emp.employeeId);
          return [
            `${emp.firstName || ''} ${emp.lastName || ''}`,
            emp.position || 'N/A',
            emp.department?.name || 'N/A',
            emp.office?.name || 'N/A',
            employeeContract?.type || 'N/A',
            emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString() : 'N/A',
            emp.email || 'N/A',
            emp.phoneNumber || 'N/A',
          ];
        }),
      ];
      const detailsWs = XLSX.utils.aoa_to_sheet(detailsData);
      detailsWs['!cols'] = [
        { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
        { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 15 }
      ];
      for (let C = 0; C <= 7; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!detailsWs[cellAddress]) detailsWs[cellAddress] = { v: '' };
        detailsWs[cellAddress].s = {
          font: { bold: true, sz: 16, color: { rgb: "3B82F6" } },
          fill: { fgColor: { rgb: "F3F4F6" } },
          alignment: { horizontal: "center" }
        };
      }
      for (let C = 0; C <= 7; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 2, c: C });
        if (!detailsWs[cellAddress]) detailsWs[cellAddress] = { v: '' };
        detailsWs[cellAddress].s = {
          font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "3B82F6" } },
          alignment: { horizontal: "center" }
        };
      }
      for (let R = 3; R < detailsData.length; R++) {
        for (let C = 0; C <= 7; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!detailsWs[cellAddress]) detailsWs[cellAddress] = { v: '' };
          detailsWs[cellAddress].s = {
            font: { sz: 11, color: { rgb: "1F2937" } },
            fill: { fgColor: { rgb: R % 2 === 0 ? "FFFFFF" : "F9FAFB" } },
            alignment: { horizontal: "left" }
          };
          if (C === 0) {
            detailsWs[cellAddress].s.font.bold = true;
          }
        }
      }
      XLSX.utils.book_append_sheet(wb, detailsWs, detailsSheetName);
      XLSX.writeFile(wb, 'employee-overview-report.xlsx');
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error generating Excel file. Please try again.');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box className="employee-overview__loading">
        <CircularProgress className="employee-overview__spinner" />
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          .employee-overview {
            min-height: 100vh;
            padding: 10px;
            background-color: transparent;
            font-family: 'Helvetica', 'Arial', sans-serif;
            transition: background-color 0.3s;
          }
          .employee-overview.dark {
            background-color: #1a202c;
          }
          .employee-overview__content {
            margin: 0 auto;
          }
          .employee-overview__button--secondary:hover {
            transform: translateY(-2px);
            background-color: rgba(229, 231, 235, 0.9);
          }
          .employee-overview.dark .employee-overview__button--secondary:hover {
            background-color: rgba(75, 85, 99, 0.9);
          }
          .employee-overview__filter-section {
            background-color: rgba(255, 255, 255, 0.95);
            padding: 24px;
            border-radius: 24px;
            margin-bottom: 32px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(229, 231, 235, 0.8);
            backdrop-filter: blur(10px);
          }
          .employee-overview.dark .employee-overview__filter-section {
            background-color: rgba(31, 41, 55, 0.95);
            border: 1px solid rgba(55, 65, 81, 0.8);
          }
          .employee-overview__filter-header {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            gap: 12px;
          }
          @media (min-width: 640px) {
            .employee-overview__filter-header {
              flex-direction: row;
              align-items: center;
            }
          }
          .employee-overview__filter-title {
            font-size: 20px;
            font-weight: bold;
            color: #3b82f6;
          }
          .employee-overview.dark .employee-overview__filter-title {
            color: #60a5fa;
          }
          .employee-overview__result-count {
            font-size: 14px;
            color: #6b7280;
          }
          .employee-overview.dark .employee-overview__result-count {
            color: #9ca3af;
          }
          .employee-overview__filter-controls {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
          }
          @media (min-width: 768px) {
            .employee-overview__filter-controls {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (min-width: 1024px) {
            .employee-overview__filter-controls {
              grid-template-columns: repeat(4, 1fr);
            }
          }
          .employee-overview__select {
            background-color: rgba(255, 255, 255, 0.8);
            border: 2px solid #e5e7eb;
            border-radius: 16px;
            font-size: 14px;
            padding: 6px 12px;
            box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
            transition: all 0.3s;
            width: 100%;
          }
          .employee-overview.dark .employee-overview__select {
            background-color: rgba(55, 65, 81, 0.8);
            border: 2px solid #4b5563;
          }
          .employee-overview__select:focus {
            border-color: #3b82f6;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px);
          }
          .employee-overview__select .MuiSelect-icon {
            display: none;
          }
          .employee-overview__select .MuiSelect-select {
            padding-right: 16px !important;
            padding: 6px 12px;
          }
          .employee-overview__select-menu {
            border-radius: 16px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          .employee-overview__loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          }
          .employee-overview__spinner {
            color: #3b82f6;
          }
          .employee-overview__dashboard-grid {
            margin-bottom: 32px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 24px;
          }
          @media (min-width: 640px) {
            .employee-overview__dashboard-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (min-width: 768px) {
            .employee-overview__dashboard-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          .employee-overview__card {
            background-color: rgba(255, 255, 255, 0.95);
            padding: 20px;
            border-radius: 24px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(229, 231, 235, 0.8);
            backdrop-filter: blur(10px);
            transition: all 0.3s;
          }
          .employee-overview.dark .employee-overview__card {
            background-color: rgba(31, 41, 55, 0.95);
            border: 1px solid rgba(55, 65, 81, 0.8);
          }
          .employee-overview__card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          }
          .employee-overview__stat-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .employee-overview__stat-label {
            font-size: 14px;
            color: #6b7280;
          }
          .employee-overview.dark .employee-overview__stat-label {
            color: #9ca3af;
          }
          .employee-overview__stat-value {
            font-size: 24px;
            font-weight: bold;
            color: #1f2937;
          }
          .employee-overview.dark .employee-overview__stat-value {
            color: #e5e7eb;
          }
          .employee-overview__icon-container {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .employee-overview__icon-container.blue {
            background-color: #3b82f6;
          }
          .employee-overview__icon-container.green {
            background-color: #10b981;
          }
          .employee-overview__icon-container.red {
            background-color: #ef4444;
          }
          .employee-overview__icon {
            width: 24px;
            height: 24px;
            color: #ffffff;
          }
          .employee-overview__charts-grid {
            margin-bottom: 32px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 24px;
          }
          @media (min-width: 1024px) {
            .employee-overview__charts-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          .employee-overview__chart-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .employee-overview__chart-title {
            font-size: 18px;
            font-weight: 600;
            color: #1f2937;
          }
          .employee-overview.dark .employee-overview__chart-title {
            color: #e5e7eb;
          }
          .employee-overview__chart-subtitle {
            font-size: 14px;
            color: #6b7280;
          }
          .employee-overview.dark .employee-overview__chart-subtitle {
            color: #9ca3af;
          }
          .employee-overview__chart-container {
            height: 300px;
            position: relative;
          }
        `}
      </style>
      <Box className="employee-overview">
        <Box className="employee-overview__content">
          <PageHeader
            title="Employee Dashboard"
            subtitle="Comprehensive workforce analytics and insights"
            actions={
              <ExportButtons
                onExportPDF={generateVisualPDF}
                onExportExcel={generateExcel}
                isExporting={loading}
              />
            }
          />
          <Card className="employee-overview__filter-section">
            <Box className="employee-overview__filter-header">
              <Typography className="employee-overview__filter-title">
                Filters
              </Typography>
              <Typography className="employee-overview__result-count">
                {filteredEmployees.length} employees
              </Typography>
            </Box>
            <Box className="employee-overview__filter-controls">
              <FormControl className="employee-overview__select">
                <Select
                  size="small"
                  value={filters.office}
                  onChange={(e) => handleFilterChange('office', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => selected === 'all' ? 'All Offices' : selected}
                >
                  <MenuItem value="all">All Offices</MenuItem>
                  {getUniqueValues('office', 'name').map(office => (
                    <MenuItem key={office} value={office}>{office}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="employee-overview__select">
                <Select
                  size="small"
                  value={filters.department}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => selected === 'all' ? 'All Departments' : selected}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {getUniqueValues('department', 'name').map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="employee-overview__select">
                <Select
                  size="small"
                  value={filters.contractType}
                  onChange={(e) => handleFilterChange('contractType', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => selected === 'all' ? 'All Contracts' : selected}
                >
                  <MenuItem value="all">All Contracts</MenuItem>
                  {['CDI', 'CDD', 'Internship', 'PSC', 'ANAPEC'].map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="employee-overview__select">
                <Select
                  size="small"
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    const labels = {
                      all: 'All Time',
                      thisMonth: 'This Month',
                      thisQuarter: 'This Quarter',
                    };
                    return labels[selected] || 'Select Date Range';
                  }}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="thisQuarter">This Quarter</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Card>
          <div ref={kpiSectionRef}>
            <Grid container className="employee-overview__dashboard-grid">
              <Grid item xs={12} sm={6} md={4}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__stat-container">
                    <Box className="employee-overview__stat-text">
                      <Typography className="employee-overview__stat-label">Total Employees</Typography>
                      <Typography className="employee-overview__stat-value">{kpiData.totalEmployees}</Typography>
                    </Box>
                    <Box className="employee-overview__icon-container blue">
                      <Person className="employee-overview__icon" />
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__stat-container">
                    <Box className="employee-overview__stat-text">
                      <Typography className="employee-overview__stat-label">New Hires</Typography>
                      <Typography className="employee-overview__stat-value">{kpiData.newHires}</Typography>
                    </Box>
                    <Box className="employee-overview__icon-container green">
                      <AssignmentInd className="employee-overview__icon" />
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__stat-container">
                    <Box className="employee-overview__stat-text">
                      <Typography className="employee-overview__stat-label">Average Tenure</Typography>
                      <Typography className="employee-overview__stat-value">
                        {kpiData.averageTenure.years}y {kpiData.averageTenure.months}m
                      </Typography>
                    </Box>
                    <Box className="employee-overview__icon-container red">
                      <ReceiptLong className="employee-overview__icon" />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </div>
          <div ref={chartsRowRef}>
            <Grid container className="employee-overview__charts-grid">
              <Grid item xs={12} lg={12}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__chart-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography className="employee-overview__chart-title">Employees by Age Distribution</Typography>
                      <Typography className="employee-overview__chart-subtitle">
                        Demographic breakdown across age ranges
                      </Typography>
                    </Box>
                    {averageAge && (
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                        color: '#1D4ED8',
                        px: 1.5,
                        py: 0.6,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Average Age: <strong>{averageAge} years</strong>
                      </Box>
                    )}
                  </Box>
                  <Box className="employee-overview__chart-container">
                    <Bar ref={ageDistributionChartRef} data={getAgeDistributionChartData()} options={horizontalChartOptions} />
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__chart-header">
                    <Box>
                      <Typography className="employee-overview__chart-title">Employees by Department</Typography>
                      <Typography className="employee-overview__chart-subtitle">Distribution across departments</Typography>
                    </Box>
                  </Box>
                  <Box className="employee-overview__chart-container">
                    <Bar ref={departmentChartRef} data={getDepartmentChartData()} options={horizontalChartOptions} />
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__chart-header">
                    <Box>
                      <Typography className="employee-overview__chart-title">FTE Allocation by Position</Typography>
                      <Typography className="employee-overview__chart-subtitle">Total FTE per position (weighted by assignment)</Typography>
                    </Box>
                  </Box>
                  <Box className="employee-overview__chart-container">
                    <Bar ref={positionChartRef} data={getPositionChartData()} options={horizontalChartOptions} />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </div>
          <div ref={analyticsRef}>
            <Grid container className="employee-overview__charts-grid">
              <Grid item xs={12} lg={6}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__chart-header">
                    <Box>
                      <Typography className="employee-overview__chart-title">Employees by Contract Type</Typography>
                      <Typography className="employee-overview__chart-subtitle">Distribution by contract types</Typography>
                    </Box>
                  </Box>
                  <Box className="employee-overview__chart-container">
                    <Pie ref={contractTypeChartRef} data={getContractTypeChartData()} options={pieChartOptions} />
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card className="employee-overview__card">
                  <Box className="employee-overview__chart-header">
                    <Box>
                      <Typography className="employee-overview__chart-title">Employees by Office Location</Typography>
                      <Typography className="employee-overview__chart-subtitle">Distribution across offices</Typography>
                    </Box>
                  </Box>
                  <Box className="employee-overview__chart-container">
                    <Bar ref={officeLocationChartRef} data={getOfficeLocationChartData()} options={horizontalChartOptions} />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </div>
        </Box>
      </Box>
    </>
  );
};

export default EmployeeOverview;